import { useMemo, useState } from "react";
import { TrendingUp, Users } from "lucide-react";
import { Slider } from "@/components/ui/slider";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

export function RoiCalculator() {
  const [aov, setAov] = useState(1200);
  const [traffic, setTraffic] = useState(15000);

  const { todayPays, recovery } = useMemo(() => {
    const todayPays = aov / 4;
    const recovery = aov * traffic * 0.02 * 0.15;
    return { todayPays, recovery };
  }, [aov, traffic]);

  return (
    <section id="roi" className="section bg-surface">
      <div className="container-tight">
        <div className="mx-auto max-w-2xl text-center">
          <span className="eyebrow">ROI Calculator</span>
          <h2 className="mt-3 text-2xl font-bold md:text-4xl">See the revenue you&apos;re leaving on the table.</h2>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 rounded-2xl border border-border bg-card p-6 shadow-soft lg:col-span-3">
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-sm font-semibold text-foreground">Average Order Value</label>
                <span className="text-xl font-bold text-primary">{formatCurrency(aov)}</span>
              </div>
              <Slider value={[aov]} min={100} max={5000} step={50} onValueChange={(v) => setAov(v[0])} className="mt-3" />
              <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                <span>$100</span>
                <span>$5,000</span>
              </div>
            </div>

            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-sm font-semibold text-foreground">Monthly Traffic</label>
                <span className="text-xl font-bold text-primary">{traffic.toLocaleString()}</span>
              </div>
              <Slider value={[traffic]} min={1000} max={100000} step={500} onValueChange={(v) => setTraffic(v[0])} className="mt-3" />
              <div className="mt-1.5 flex justify-between text-xs text-muted-foreground">
                <span>1,000</span>
                <span>100,000</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 lg:col-span-2">
            <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Users className="h-4 w-4 text-primary" />
                Customer Pays Today
              </div>
              <div className="mt-2">
                <span className="text-sm text-muted-foreground line-through">{formatCurrency(aov)}</span>
                <div className="text-3xl font-bold text-foreground">{formatCurrency(todayPays)}</div>
                <p className="mt-1 text-xs text-muted-foreground">Split 4 ways — no debt, no interest.</p>
              </div>
            </div>

            <div className="rounded-2xl bg-gradient-primary p-5 text-primary-foreground shadow-elevated">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-90">
                <TrendingUp className="h-4 w-4" />
                Projected Monthly Recovery
              </div>
              <div className="mt-2 text-3xl font-bold">{formatCurrency(recovery)}</div>
              <p className="mt-1 text-xs opacity-90">Based on recovering 15% of the 2% of premium carts typically lost to abandonment.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
